import {DatePicker} from 'antd';
import dayjs from 'dayjs';

import {Locale} from '../../../../../provider/locale/locale';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {useDateTimePickerDisabler} from '../../../../../service/datepicker/datetime-picker-hook';
import {useFormRules} from '../../../../../service/form/form-rules-hook';
import {
    YandexStoryFormFieldsEnum,
    YandexStoryFormType,
} from '../../../../../service/yandex-stories/yandex-stories-type';
import {Form, FormInstance} from '../../../../../typings/antd';

import {YANDEX_STORY_DATE_TIME_FORMAT, YANDEX_STORY_TIME_FORMAT} from './yandex-story-confirmation-step-helper';
import * as styles from './yandex-story-schedule-fields.scss';

type PropsType = {
    formInstance: FormInstance<YandexStoryFormType>;
    formMode: 'create' | 'edit';
};

export function YandexStoryScheduleFields(props: PropsType): JSX.Element {
    const {formInstance, formMode} = props;

    const {getLocalizedString} = useLocale();
    const {validateDayjsAfterRule, validateDayjsInTheFutureRule} = useFormRules();

    const publishAt = Form.useWatch(YandexStoryFormFieldsEnum.PublishAt, {form: formInstance, preserve: true});
    const deleteAt = Form.useWatch(YandexStoryFormFieldsEnum.DeleteAt, {form: formInstance, preserve: true});

    // Scheduling only moves forward in time: a story cannot be published in the past and cannot be
    // deleted before it is published. The catalog rejects such dates with a raw API message, so the
    // pickers hide them and the form validates them itself.
    const {dateDisabled: publishDateDisabled, timeDisabled: publishTimeDisabled} = useDateTimePickerDisabler({
        after: dayjs(),
        before: deleteAt,
    });

    const {dateDisabled: deleteDateDisabled, timeDisabled: deleteTimeDisabled} = useDateTimePickerDisabler({
        after: publishAt || dayjs(),
    });

    const dateInFutureRule = validateDayjsInTheFutureRule(
        getLocalizedString('YANDEX_STORY_FORM__CONFIRMATION_STEP__VALIDATION__DATE_IN_FUTURE')
    );

    const deleteAfterPublishRules = publishAt
        ? [
              validateDayjsAfterRule(
                  getLocalizedString('YANDEX_STORY_FORM__CONFIRMATION_STEP__VALIDATION__DELETE_AFTER_PUBLISH'),
                  publishAt.toDate()
              ),
          ]
        : [];

    // An already published story keeps its publish date in the past, so the future check
    // only applies while the story is being created.
    const publishAtRules = formMode === 'create' ? [dateInFutureRule] : [];

    return (
        <div className={styles.YandexStoryScheduleFields}>
            <Form.Item<YandexStoryFormType>
                className={styles.YandexStoryScheduleFields_field}
                dependencies={[YandexStoryFormFieldsEnum.DeleteAt]}
                label={<Locale stringKey="YANDEX_STORY_FORM__CONFIRMATION_STEP__PUBLISH_AT" />}
                name={YandexStoryFormFieldsEnum.PublishAt}
                rules={publishAtRules}
            >
                <DatePicker
                    disabledDate={publishDateDisabled}
                    disabledTime={publishTimeDisabled}
                    format={YANDEX_STORY_DATE_TIME_FORMAT}
                    showTime={{format: YANDEX_STORY_TIME_FORMAT}}
                />
            </Form.Item>

            <Form.Item<YandexStoryFormType>
                className={styles.YandexStoryScheduleFields_field}
                dependencies={[YandexStoryFormFieldsEnum.PublishAt]}
                label={<Locale stringKey="YANDEX_STORY_FORM__CONFIRMATION_STEP__DELETE_AT" />}
                name={YandexStoryFormFieldsEnum.DeleteAt}
                rules={[dateInFutureRule, ...deleteAfterPublishRules]}
            >
                <DatePicker
                    disabledDate={deleteDateDisabled}
                    disabledTime={deleteTimeDisabled}
                    format={YANDEX_STORY_DATE_TIME_FORMAT}
                    showTime={{format: YANDEX_STORY_TIME_FORMAT}}
                />
            </Form.Item>
        </div>
    );
}
